import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Button,
  useColorModeValue,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Select,
  Textarea,
  FormControl,
  FormLabel,
  Progress,
  IconButton,
  Tooltip,
  Image,
  Skeleton,
  Collapse,
} from '@chakra-ui/react';
import { useCustomToast } from '../utils/customToast';
import { 
  FaImage, 
  FaArrowRight,
  FaGoogle,
  FaInstagram,
  FaFacebook,
  FaDesktop,
  FaUsers,
  FaBullhorn,
  FaMagic,
  FaAd,
  FaDownload,
  FaCopy,
  FaChevronDown,
  FaChevronUp,
  FaRedo,
  FaLightbulb,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';
import {
  getVerticalTemplates,
  generateTemplates,
  CONTENT_TYPE_INFO,
} from '../services/contentTemplateService';

// Icon mapping for content types
const CONTENT_ICONS = {
  gmb_post: FaGoogle,
  website_hero: FaDesktop,
  about_page: FaUsers,
  social_instagram: FaInstagram,
  social_facebook: FaFacebook,
  paid_ad_display: FaBullhorn,
  paid_ad_social: FaAd,
};

// Color mapping for content types
const CONTENT_COLORS = {
  gmb_post: 'blue',
  website_hero: 'purple',
  about_page: 'teal',
  social_instagram: 'pink',
  social_facebook: 'facebook',
  paid_ad_display: 'orange',
  paid_ad_social: 'red',
};

// Content type options for selection
const CONTENT_TYPE_OPTIONS = [
  { value: 'gmb_post', label: 'GMB Post', icon: FaGoogle, color: 'blue' },
  { value: 'website_hero', label: 'Website Hero', icon: FaDesktop, color: 'purple' },
  { value: 'social_instagram', label: 'Instagram', icon: FaInstagram, color: 'pink' },
  { value: 'social_facebook', label: 'Facebook', icon: FaFacebook, color: 'facebook' },
  { value: 'paid_ad_display', label: 'Display Ad', icon: FaBullhorn, color: 'orange' },
  { value: 'paid_ad_social', label: 'Social Ad', icon: FaAd, color: 'red' },
];

// Industry-specific content strategies (condensed version)
const INDUSTRY_STRATEGIES = {
  junk_removal: {
    industry: 'Junk Removal',
    overview: 'Visual proof of transformation is everything. Before/after photos showcase your team\'s efficiency.',
    platforms: {
      social: { title: 'Social Media', tip: 'Before/after transformations, time-lapse cleanouts, team photos' },
      website: { title: 'Website', tip: 'Branded trucks, project gallery by type, trust badges' },
      gmb: { title: 'Google My Business', tip: 'Action shots, before/after, fleet images, eco-friendly disposal' }
    }
  },
  hvac: {
    industry: 'HVAC',
    overview: 'Emphasize reliability and technical expertise. Show the comfort you bring to homes.',
    platforms: {
      social: { title: 'Social Media', tip: 'Seasonal tips, energy advice, installations, weather reminders' },
      website: { title: 'Website', tip: 'Completed installations, certifications, financing graphics' },
      gmb: { title: 'Google My Business', tip: 'Service vehicles, uniformed techs, completed work' }
    }
  },
  hotel: {
    industry: 'Hotels & Hospitality',
    overview: 'Transport guests into the experience before they arrive with stunning visuals.',
    platforms: {
      social: { title: 'Social Media', tip: 'Room photos, amenities, local attractions, guest experiences' },
      website: { title: 'Website', tip: 'High-res galleries, virtual tours, event spaces' },
      gmb: { title: 'Google My Business', tip: 'Exterior, lobby, room types, dining, pool/fitness' }
    }
  },
  restaurant: {
    industry: 'Restaurants',
    overview: 'Make mouths water and convey your unique atmosphere. Food content drives action.',
    platforms: {
      social: { title: 'Social Media', tip: 'Plated dishes, chef videos, menu highlights, celebrations' },
      website: { title: 'Website', tip: 'Menu photography, interior ambiance, team photos' },
      gmb: { title: 'Google My Business', tip: 'Signature dishes, seating areas, storefront' }
    }
  },
  automotive: {
    industry: 'Automotive',
    overview: 'Convey trust and expertise for significant financial decisions.',
    platforms: {
      social: { title: 'Social Media', tip: 'Inventory spotlights, tips, customer deliveries, staff' },
      website: { title: 'Website', tip: 'Multi-angle inventory, service department, facility tours' },
      gmb: { title: 'Google My Business', tip: 'Showroom, service bays, lounge, featured vehicles' }
    }
  },
  healthcare: {
    industry: 'Healthcare',
    overview: 'Balance professionalism with approachability. Build confidence and trust.',
    platforms: {
      social: { title: 'Social Media', tip: 'Health tips, staff spotlights, community involvement' },
      website: { title: 'Website', tip: 'Provider headshots, facility tours, welcoming spaces' },
      gmb: { title: 'Google My Business', tip: 'Building exterior, modern interior, friendly staff' }
    }
  },
  retail: {
    industry: 'Retail',
    overview: 'Create desire and urgency. Showcase products beautifully.',
    platforms: {
      social: { title: 'Social Media', tip: 'New arrivals, styling inspiration, flash sales, events' },
      website: { title: 'Website', tip: 'Product photography, lifestyle imagery, store photos' },
      gmb: { title: 'Google My Business', tip: 'Storefront, shopping areas, featured products' }
    }
  },
  generic_business: {
    industry: 'Your Business',
    overview: 'Showcase what makes you unique while building trust with potential customers.',
    platforms: {
      social: { title: 'Social Media', tip: 'Behind-the-scenes, team highlights, customer success' },
      website: { title: 'Website', tip: 'Hero images, team photos, service galleries' },
      gmb: { title: 'Google My Business', tip: 'Exterior, interior, team at work, your brand' }
    }
  }
};

// Get industry strategy with fallback
const getIndustryStrategy = (vertical) => {
  if (!vertical) return INDUSTRY_STRATEGIES.generic_business;
  const verticalLower = vertical.toLowerCase().replace(/-/g, '_');
  
  for (const [key, strategy] of Object.entries(INDUSTRY_STRATEGIES)) {
    if (verticalLower.includes(key) || key.includes(verticalLower)) {
      return strategy;
    }
  }
  
  return {
    ...INDUSTRY_STRATEGIES.generic_business,
    industry: vertical.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  };
};

/**
 * Platform Strategy Mini Card Component
 */
const PlatformMiniCard = ({ title, tip, icon: PlatformIcon, colorScheme }) => {
  const cardBg = useColorModeValue('white', 'gray.750');
  const borderColor = useColorModeValue('gray.100', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      p={3}
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      _hover={{
        shadow: 'sm',
        borderColor: `${colorScheme}.200`,
      }}
    >
      <HStack spacing={2} mb={2}>
        <Flex
          w="28px"
          h="28px"
          bgGradient={`linear(135deg, ${colorScheme}.400, ${colorScheme}.600)`}
          borderRadius="lg"
          justify="center"
          align="center"
        >
          <Icon as={PlatformIcon} color="white" boxSize={3} />
        </Flex>
        <Text fontWeight="semibold" fontSize="sm">
          {title}
        </Text>
      </HStack>
      <Text fontSize="xs" color={textColor} lineHeight="1.5">
        {tip}
      </Text>
    </Box>
  );
};

/**
 * Template Preview Card for Widget
 */
const TemplatePreviewCard = ({ template, onClick }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useCustomToast();

  const ContentIcon = CONTENT_ICONS[template.content_type] || FaImage;
  const colorScheme = CONTENT_COLORS[template.content_type] || 'gray';
  const contentInfo = CONTENT_TYPE_INFO[template.content_type] || {};

  const handleDownload = (e) => {
    e.stopPropagation();
    window.open(template.image_url, '_blank');
  };

  const handleCopyUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(template.image_url);
    toast({
      title: 'URL copied!',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      shadow="sm"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        shadow: 'md',
        transform: 'translateY(-2px)',
        borderColor: `${colorScheme}.300`,
      }}
      onClick={onClick}
      overflow="hidden"
    >
      <Box position="relative" overflow="hidden">
        <Image
          src={template.image_url}
          alt={template.title}
          objectFit="cover"
          h="120px"
          w="100%"
          fallback={<Skeleton h="120px" />}
        />
        <Badge
          position="absolute"
          top={2}
          left={2}
          colorScheme={colorScheme}
          variant="solid"
          fontSize="2xs"
        >
          <Icon as={ContentIcon} mr={1} />
          {contentInfo.name?.split(' ')[0] || template.content_type}
        </Badge>
      </Box>
      <Box p={2}>
        <Text fontSize="xs" fontWeight="medium" noOfLines={1} mb={1}>
          {template.title}
        </Text>
        <HStack spacing={1}>
          <Tooltip label="Download">
            <IconButton
              icon={<FaDownload />}
              size="xs"
              variant="ghost"
              colorScheme="blue"
              onClick={handleDownload}
              aria-label="Download"
            />
          </Tooltip>
          <Tooltip label="Copy URL">
            <IconButton
              icon={<FaCopy />}
              size="xs"
              variant="ghost"
              colorScheme="green"
              onClick={handleCopyUrl}
              aria-label="Copy URL"
            />
          </Tooltip>
        </HStack>
      </Box>
    </Box>
  );
};

/**
 * ContentTemplatesWidget - Enhanced widget for content templates
 * Shows industry strategy, generation controls, and recent templates
 */
const ContentTemplatesWidget = ({ reviewsData = [] }) => {
  const navigate = useNavigate();
  const toast = useCustomToast();
  const { vertical: userVertical } = useUser();
  
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [selectedContentType, setSelectedContentType] = useState('gmb_post');
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [showGenerator, setShowGenerator] = useState(false);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBorderColor = useColorModeValue('blue.200', 'blue.600');
  const strategyBg = useColorModeValue('gray.50', 'gray.750');
  const accentGradient = useColorModeValue(
    'linear(to-r, purple.500, blue.500)',
    'linear(to-r, purple.400, blue.400)'
  );
  const hoverGradient = useColorModeValue(
    'linear(to-r, purple.600, blue.600)',
    'linear(to-r, purple.500, blue.500)'
  );

  // Get industry strategy
  const strategy = useMemo(() => getIndustryStrategy(userVertical), [userVertical]);

  // Get unique business names from reviewsData
  const businesses = useMemo(() => {
    const unique = new Set(reviewsData.map(r => r.name).filter(Boolean));
    return Array.from(unique).sort();
  }, [reviewsData]);

  // Load cached templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true);
      try {
        const verticalKey = userVertical || 'generic_business';
        const templateData = await getVerticalTemplates(verticalKey);
        setTemplates(templateData.templates || []);
      } catch (e) {
        // No cached templates - that's okay
        setTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [userVertical]);

  // Handle generate
  const handleGenerate = useCallback(async () => {
    if (!selectedContentType) {
      toast({
        title: 'Select Content Type',
        description: 'Please select a content type to generate',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(10);

    try {
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 15, 90));
      }, 2000);

      const verticalKey = userVertical || 'generic_business';
      const result = await generateTemplates(verticalKey, [selectedContentType], {
        businessName: selectedBusiness || undefined,
        customPrompt: customPrompt || undefined,
      });

      clearInterval(progressInterval);
      setGenerationProgress(100);

      if (result.success) {
        setTemplates(prev => [...(result.templates || []), ...prev]);
        toast({
          title: result.cached ? 'Template Loaded' : 'Template Generated!',
          description: result.cached
            ? 'Using cached template'
            : `Generated new ${CONTENT_TYPE_OPTIONS.find(o => o.value === selectedContentType)?.label || 'template'}`,
          status: 'success',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Generation issue',
          description: result.error || 'Template failed to generate',
          status: 'warning',
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  }, [selectedContentType, selectedBusiness, customPrompt, userVertical, toast]);

  // Get recent templates (last 4)
  const recentTemplates = useMemo(() => templates.slice(0, 4), [templates]);

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      boxShadow="sm"
      _hover={{
        shadow: 'md',
        borderColor: hoverBorderColor
      }}
      transition="all 0.3s"
    >
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Icon as={FaMagic} color="purple.500" />
            <Heading
              size="md"
              textTransform="uppercase"
              fontStyle="italic"
              fontSize="1.3em"
            >
              Content Templates
            </Heading>
          </HStack>
          <Button
            size="sm"
            colorScheme="purple"
            variant="ghost"
            rightIcon={<Icon as={FaArrowRight} />}
            onClick={() => navigate('/dashboards/content-templates')}
          >
            View All
          </Button>
        </Flex>
        <Text fontSize="sm" color={subtitleColor} mt={1}>
          AI-generated marketing templates for your business
        </Text>
      </CardHeader>

      <CardBody pt={0}>
        {isLoading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="lg" color="purple.500" />
          </Flex>
        ) : (
          <VStack spacing={4} align="stretch">
            {/* Industry Content Strategy Section */}
            <Box
              bg={strategyBg}
              borderRadius="xl"
              p={4}
              borderWidth="1px"
              borderColor={borderColor}
            >
              <HStack spacing={2} mb={3}>
                <Icon as={FaLightbulb} color="yellow.500" boxSize={4} />
                <Badge
                  bgGradient="linear(135deg, blue.500, cyan.500)"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {strategy.industry}
                </Badge>
                <Text fontSize="xs" color={subtitleColor}>
                  Content Strategy
                </Text>
              </HStack>
              
              <Text fontSize="sm" color={textColor} mb={4} lineHeight="1.6">
                {strategy.overview}
              </Text>

              {/* Platform Strategy Cards */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <PlatformMiniCard
                  title={strategy.platforms.social.title}
                  tip={strategy.platforms.social.tip}
                  icon={FaInstagram}
                  colorScheme="pink"
                />
                <PlatformMiniCard
                  title={strategy.platforms.website.title}
                  tip={strategy.platforms.website.tip}
                  icon={FaDesktop}
                  colorScheme="purple"
                />
                <PlatformMiniCard
                  title={strategy.platforms.gmb.title}
                  tip={strategy.platforms.gmb.tip}
                  icon={FaGoogle}
                  colorScheme="blue"
                />
              </SimpleGrid>
            </Box>

            {/* Quick Generate Section */}
            <Box>
              <Button
                w="100%"
                variant="ghost"
                size="sm"
                onClick={() => setShowGenerator(!showGenerator)}
                rightIcon={showGenerator ? <FaChevronUp /> : <FaChevronDown />}
                justifyContent="space-between"
                fontWeight="semibold"
                color={textColor}
                mb={2}
              >
                <HStack>
                  <Icon as={FaMagic} color="purple.500" />
                  <Text>Quick Generate</Text>
                </HStack>
              </Button>

              <Collapse in={showGenerator} animateOpacity>
                <VStack spacing={3} align="stretch" p={3} bg={strategyBg} borderRadius="lg">
                  {/* Business Selector */}
                  <FormControl size="sm">
                    <FormLabel fontSize="xs" fontWeight="medium" mb={1}>
                      Business Name
                    </FormLabel>
                    <Select
                      placeholder={businesses.length > 0 ? "Select a business" : "No businesses available"}
                      value={selectedBusiness}
                      onChange={(e) => setSelectedBusiness(e.target.value)}
                      isDisabled={isGenerating || businesses.length === 0}
                      size="sm"
                    >
                      {businesses.map(name => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Content Type Selection */}
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="medium" mb={1}>
                      Content Type
                    </FormLabel>
                    <Flex flexWrap="wrap" gap={2}>
                      {CONTENT_TYPE_OPTIONS.map(option => {
                        const isSelected = selectedContentType === option.value;
                        const IconComponent = option.icon;
                        return (
                          <Button
                            key={option.value}
                            size="xs"
                            variant={isSelected ? "solid" : "outline"}
                            colorScheme={isSelected ? option.color : "gray"}
                            borderRadius="full"
                            px={3}
                            onClick={() => setSelectedContentType(option.value)}
                            isDisabled={isGenerating}
                            leftIcon={<Icon as={IconComponent} boxSize={3} />}
                            fontWeight="medium"
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </Flex>
                  </FormControl>

                  {/* Custom Prompt */}
                  <FormControl>
                    <FormLabel fontSize="xs" fontWeight="medium" mb={1}>
                      Custom Prompt (optional)
                    </FormLabel>
                    <Textarea
                      placeholder="E.g., 'sunny day, friendly team, professional look'"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      isDisabled={isGenerating}
                      size="sm"
                      rows={2}
                      fontSize="xs"
                    />
                  </FormControl>

                  {/* Generate Button */}
                  <Button
                    size="sm"
                    colorScheme="purple"
                    leftIcon={<FaMagic />}
                    onClick={handleGenerate}
                    isLoading={isGenerating}
                    loadingText="Generating..."
                    isDisabled={!selectedContentType}
                  >
                    Generate Template
                  </Button>

                  {/* Generation Progress */}
                  {isGenerating && (
                    <Progress
                      value={generationProgress}
                      size="xs"
                      colorScheme="purple"
                      borderRadius="full"
                      hasStripe
                      isAnimated
                    />
                  )}
                </VStack>
              </Collapse>
            </Box>

            {/* Recent Templates */}
            {recentTemplates.length > 0 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                    Recent Templates
                  </Text>
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    {templates.length} cached
                  </Badge>
                </HStack>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                  {recentTemplates.map(template => (
                    <TemplatePreviewCard
                      key={template.template_id}
                      template={template}
                      onClick={() => navigate('/dashboards/content-templates')}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Empty State for Templates */}
            {templates.length === 0 && !isGenerating && (
              <Box
                textAlign="center"
                py={4}
                px={3}
                bg={strategyBg}
                borderRadius="lg"
              >
                <Icon as={FaImage} boxSize={8} color="gray.400" mb={2} />
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                  No Templates Yet
                </Text>
                <Text fontSize="xs" color={subtitleColor}>
                  Use Quick Generate above or visit the full dashboard to create AI-powered marketing visuals
                </Text>
              </Box>
            )}

            {/* CTA Button to Full Dashboard */}
            <Button
              w="100%"
              size="md"
              bgGradient={accentGradient}
              color="white"
              _hover={{ bgGradient: hoverGradient }}
              leftIcon={<Icon as={FaImage} />}
              rightIcon={<Icon as={FaArrowRight} />}
              onClick={() => navigate('/dashboards/content-templates')}
            >
              Open Content Studio
            </Button>
          </VStack>
        )}
      </CardBody>
    </Card>
  );
};

export default ContentTemplatesWidget;
